import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import type {
  BoardVisibility,
  BoardVisibilityPolicyRule,
  WorkspaceGuestInviteRule,
  WorkspaceProfilePatch,
  WorkspaceRole,
  WorkspaceVisibility,
} from '@molt/shared';
import { Button, Dialog, EmptyState, Notice, Skeleton, profileName } from '@/components';
import { WorkspaceMemberRemoveDialog, WorkspaceSettingsPanel } from '@/components';
import {
  WorkspaceBoardVisibilityPolicySection,
  type WorkspaceBoardVisibilityPolicySectionProps,
} from '@/components/workspace/WorkspaceBoardVisibilityPolicySection';
import {
  WorkspaceGuestAccessSection,
  type WorkspaceGuestAccessSectionProps,
} from '@/components/workspace/WorkspaceGuestAccessSection';
import {
  WorkspaceMemberList,
  type WorkspaceMemberListProps,
} from '@/components/workspace/WorkspaceMemberList';
import { WorkspaceJoinRequestsSection } from '@/components/workspace/WorkspaceJoinRequestsSection';
import { announce } from '@/lib/announce';
import { useWorkspaceEnterpriseLinkage } from '@/lib/enterprise';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  useApproveWorkspaceJoinRequest,
  useDeclineWorkspaceJoinRequest,
  useWorkspaceJoinRequests,
} from '@/lib/invitations';
import { isSuperadminSession, signInHref, useSession } from '@/lib/session';
import {
  ownRole,
  useAddWorkspaceMember,
  useDeleteWorkspace,
  useLeaveWorkspace,
  useRemoveWorkspaceGuest,
  useRemoveWorkspaceMember,
  useUpdateWorkspaceBoardVisibilityPolicy,
  useUpdateWorkspaceGuestInviteRule,
  useUpdateWorkspaceProfile,
  useUpdateWorkspaceMemberRole,
  useUpdateWorkspaceVisibility,
  useWorkspaceBoardVisibilityPolicies,
  useWorkspaceGuestInviteRule,
  useWorkspaceGuests,
  useWorkspaceMembers,
  workspaceQueryOptions,
  workspacesQueryOptions,
} from '@/lib/workspaces';

/**
 * `/w/:workspaceId/members` — `FE-07.2`, chunk `D2`, agent H.
 * `FR-PERM-014`, `FR-PERM-021`. Extended by fan-out 10's Enterprise Policy
 * Route chunk with the Enterprise cross-link (`FE-67.2`, `FE-67.3`,
 * `FR-PERM-034`) — see `enterpriseLinkage` below.
 *
 * The composition site for `WorkspaceMemberList`: the list is presentational
 * (props in, callbacks out, no `announce()`) and this file owns the session,
 * the members query, both mutations, the mapping from `ApiError` to the list's
 * plain-string message props, and every announcement. `routes/profile.tsx` is
 * the shipped shape this follows.
 *
 * ---------------------------------------------------------------------------
 * THE URL IS BY WORKSPACE ID, AND THAT IS A DECISION
 * ---------------------------------------------------------------------------
 * `workspaceSchema` carries a `shortName`, and a `/w/<short-name>/members` URL
 * would read better — but **there is no `GET /workspaces/by-short-name/:x`
 * route**, so it would need a wire that does not exist, and inventing one is a
 * contract change this chunk may not make (a frontend item that needs a schema
 * change has found a contract bug: it reports it, it does not patch it).
 *
 * ⚠ **`B39`'s workspace-settings surface extends this file** rather than
 * opening a second one, exactly as `C1` left the board route for `FE-08.4`.
 * `FR-PERM-014` AC2 and AC4's *"workspace settings"* clause is B39's, and this
 * page is where it lands. Two things B39 should know before it starts:
 *
 *  - **The admin/normal split on this page is derived from the member list
 *    itself** (`ownRole` below), because route 4 returning the caller's own row
 *    is the only source of the caller's role in the whole client. A settings
 *    surface that renders admin-only controls needs the same derivation, so it
 *    belongs **inside this component**, below the same query — not in a sibling
 *    route that would have to fetch the member list a second time to learn who
 *    the caller is.
 *  - **The heading is already a workspace-name lookup with a fallback.** If
 *    B39 adds `GET /workspaces/:id` (it is the block `queryKeys.workspaces`'s
 *    docstring names as the one that will want it), the heading should switch
 *    to it; today's cache read is deliberate, not a placeholder for a fetch
 *    this route could have made.
 *
 * ---------------------------------------------------------------------------
 * BRANCH ORDER IS LOAD-BEARING
 * ---------------------------------------------------------------------------
 * `isSignedOut` first, then `isPending`, then `profile === null`, then the
 * members query — because `useSession()` can return a **stale non-null
 * `profile` beside `isSignedOut: true`**, which D1 found and recorded. Reading
 * `profile` before `isSignedOut` would render an admin's controls out of an
 * expired session: every write 401s, and the row of role menus in front of the
 * member is a lie they will act on.
 *
 * ---------------------------------------------------------------------------
 * THE MEMBERS QUERY IS **NOT** GATED ON THE SESSION, AND THAT IS THE POINT
 * ---------------------------------------------------------------------------
 * A signed-out visitor holding this URL should land on
 * `/sign-in?next=%2Fw%2F…%2Fmembers` and come back here, and `main.tsx`'s
 * `QueryCache.onError` is the one place in the client that does that. It fires
 * on any `unauthenticated` failure **except** the shell's own `['me']` probe,
 * so a route whose only query is that probe gets no bounce at all — which is
 * why `routes/profile.tsx` renders a *Sign in* empty state instead of
 * redirecting. This route issues a second, non-excluded query, so the redirect
 * is real here. Gating `useWorkspaceMembers` on the session would remove it and
 * leave the empty state below as the only outcome.
 *
 * The signed-out branch is still rendered, for the frame before the navigation
 * commits and for the case where the binding is absent (every unit test
 * constructs its own client). It is a floor, not the mechanism.
 *
 * ⚠ **Exactly one 401-capable query on this page, deliberately.** The heading's
 * workspace name is read with `enabled: false` — a cache read, reactive,
 * issuing no request — precisely so that a signed-out visit produces **one**
 * redirect rather than two racing ones (`D2-BRIEF.md` §11.1 is a check on that
 * count). It is also §6.8 rule 4: no `GET /workspaces/:id` for a heading, on a
 * page whose real content is one route away.
 *
 * ---------------------------------------------------------------------------
 * THE THREE OUTCOMES (§5.3), AND THE FOURTH THIS FILE ADDS
 * ---------------------------------------------------------------------------
 * Route 4 answers **404** to a non-member and to a soft-deleted workspace,
 * identically and by design — a 403 there would make the route an existence
 * oracle. Route 6 answers **403** to a member who is not an admin. So:
 *
 *  - 404 → a page-level `EmptyState tone="error"`, no member list;
 *  - 200 + own row `normal` → the list, every role visible, no control that
 *    changes one (`FR-PERM-014` AC3, `FR-PERM-021` AC3);
 *  - 200 + own row `admin` → the list with role controls and the add form.
 *
 * ⚠ The fourth is **any other failure**, and folding it into the 404 copy would
 * tell a member their workspace does not exist because the server 500'd —
 * `FR-EXP-034`'s "a failed load must never render the empty copy", inverted.
 * The branch below splits on `error.code === 'not_found'` for that reason.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS ROUTE DOES NOT COMPUTE
 * ---------------------------------------------------------------------------
 * ⚠ **The last-admin rule is the server's** (`assertWorkspaceRetainsAdmin`
 * takes a `SELECT … FOR UPDATE` row lock, counts admins and throws **409**).
 * This client's copy of the member list is stale by definition and a row lock
 * is not reproducible in a browser, so the role control is always enabled and
 * the 409's `message` is surfaced verbatim. A client-side guard that disagreed
 * with the server would be a control disabled when it should work.
 *
 * ⚠ **That 409 is, today, unreachable from this surface — and the handling
 * stays.** Two of this chunk's own rules compose into it: the role control is
 * never rendered on the viewer's own row (§6.4 rule 3), and only an admin sees
 * a role control at all — so any *other* admin the viewer can demote is, by
 * construction, not the only one, and `assertWorkspaceRetainsAdmin` returns
 * without throwing. A stale list does not open the path either: if the viewer
 * has since been demoted, `requireWorkspaceAdmin` answers **403** before the
 * count is ever taken. The path opens the moment `B39` lands remove
 * (`BE-39.4`) or leave (`BE-39.5`) on this page — both call the same guard —
 * or the moment any self-demotion control exists. Surfacing it now is the
 * cheaper half of that; **pre-empting** it client-side would have been the
 * expensive one.
 *
 * ⚠ **`added` is the distinction, not the status code.** `POST
 * /workspaces/:id/members` answers **200** for both a real add and a no-op
 * re-add; `FR-PERM-021` AC4 wants the second **stated**, not reported as a
 * success and not as a failure. A surface that branched on `response.ok` gets
 * this exactly wrong, which is why the branch below reads `result.added`.
 *
 * ⚠ **A failed role change is reported once, above the list, not on the row it
 * belongs to** — `WorkspaceMemberListProps` gives no per-row anchor for it, and
 * `busyRoleFor` names only the row *currently* in flight, which a settled
 * failure has already left. ⚠ Neither server message names the member — the
 * 409 names the *rule* ("This workspace has only one admin…", which
 * `FR-PERM-032` AC1 requires it to) and the 404 names the membership — so the
 * notice above the list is a sentence about the page, not about a row, and
 * that is the honest reading of it. It cannot describe a change other than the
 * most recent one: exactly one role mutation is ever in flight from this
 * surface, and `mutate` clears the previous error. A per-row anchor is D's
 * file to add, not this one's.
 *
 * ---------------------------------------------------------------------------
 * ONE EVENT, ONE CHANNEL — AND THE ONE PLACE THAT IS NOT CLEAN
 * ---------------------------------------------------------------------------
 * §7 rule 4: a **failed** query or write speaks through the `role="alert"` the
 * component already renders (`Notice tone="bad"`, `EmptyState tone="error"`)
 * and is **not** announced from here. A settled role change and a settled add
 * have no live-region node of their own, so `announce()` is their only channel.
 *
 * ⚠ The no-op re-add is the exception, and it is a compromise rather than a
 * clean call: `addNotice` mounts a `Notice tone="info"`, which is
 * `role="status"` — a live region **inserted already populated**, which
 * assistive technology reads unreliably. So this route announces it too. The
 * cost is that a screen reader that *does* read the freshly-mounted region
 * hears the sentence twice; the alternative is a member who hears nothing at
 * all about the outcome of a write they just made. `WorkspaceMemberList`
 * forwards no rest props, so the route cannot suppress the inner region.
 * Recorded in the D2 return.
 *
 * ⚠ **The add field does not auto-clear after a successful add**, and that is
 * a stated limit rather than an oversight. `WorkspaceMemberListProps` carries
 * no success signal, so the only mechanism available to this route is a changed
 * `key` — which destroys the form's DOM including the **Add button the member
 * has just pressed and still has focus on**, dropping focus to `<body>` and
 * restarting the next Tab at the top of the document. A stale value in a field
 * is a smaller defect than a lost focus position, and the outcome is already
 * both spoken and (for a re-add) stated on screen. §5.7 already requires the
 * value to survive a 404 no-such-account so a typo can be corrected rather than
 * retyped; this simply extends to the success case. The fix is D's file, not
 * this one: an `onAdded` signal, or a `value`/`onValueChange` pair.
 */
export const Route = createFileRoute('/w/$workspaceId/members')({
  component: WorkspaceMembersRoute,
});

/** The list's own geometry, so the page does not jump when it arrives. */
function MembersPending() {
  return (
    <div aria-busy="true" className="flex flex-col gap-4">
      <Skeleton shape="block" count={3} />
    </div>
  );
}

/**
 * The page frame, and it wraps **every** state rather than only the loaded one.
 *
 * §7 fixes an `h1` for this route and `EmptyState.headingLevel` accepts only
 * `2 | 3 | 4` — deliberately, because a primitive cannot know its place in the
 * outline. An `EmptyState` returned *instead* of this frame would ship a page
 * whose highest heading is a level 2 with no level 1 above it, which is the
 * exact defect `headingLevel` was added for in `C1`, inverted. `profile.tsx`
 * carries the same note.
 */
function MembersPage({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 py-8">
      <h1 className="font-display text-2xl font-semibold tracking-tight text-ink">
        {title}
      </h1>
      {children}
    </div>
  );
}

function policyKey(
  visibility: BoardVisibility,
  field: 'createRule' | 'deleteRule',
): string {
  return `${visibility}:${field}`;
}

/**
 * FE-80.1 — the three label tables now hold CATALOGUE KEYS, and one of them is
 * gone entirely.
 *
 * ⚠ A module-level constant cannot call `t()`, so each table maps a WIRE VALUE
 * to a key and every read resolves it at render. `BoardMapView.tsx:80–84` is
 * the precedent.
 *
 * ⚠ The `Record<Enum, string>` annotations stay EXPLICIT on all three. They are
 * the only thing making a missing or misspelled arm a typecheck error, because
 * `t()` returns the key itself on a miss rather than throwing — a dropped arm
 * would speak `workspaceMembers.guests.rule.adminsOnly` into the live region
 * with nothing red anywhere.
 *
 * ⚠ The RECORD KEYS are the wire values (`workspace_visible`,
 * `workspace_admins_only`, …) and stay literal: they index a response value and
 * they are what `mutate` sends. Only the record's VALUES changed.
 *
 * ⚠ `POLICY_FIELD_LABELS` IS DELETED. It mapped `createRule → 'create'` and
 * `deleteRule → 'delete'`, and `handleChangePolicy` dropped the result into the
 * MIDDLE of a sentence. An English verb stem slotted mid-sentence is
 * untranslatable — it fixes word order, it fixes that the word is a bare stem,
 * and it fixes that the sentence needs exactly one there. The field is now the
 * CHOICE OF KEY (`boardPolicy.createSaved` / `boardPolicy.deleteSaved`) and
 * neither word gets a key of its own. The `{visibility}` noun beside it KEEPS
 * its slot, on `boardFace.template.visibilitySet`'s recorded ruling for this
 * exact noun.
 */
const POLICY_VISIBILITY_KEYS: Record<BoardVisibility, string> = {
  private: 'workspaceMembers.boardPolicy.visibility.private',
  workspace_visible: 'workspaceMembers.boardPolicy.visibility.workspace',
  enterprise_visible: 'workspaceMembers.boardPolicy.visibility.enterprise',
  public: 'workspaceMembers.boardPolicy.visibility.public',
};

/**
 * The three policy rules, for the ENTERPRISE-OVERRIDE arm of the save
 * announcement — `FE-67.2`, `FR-PERM-033`'s edge case.
 *
 * ⚠ REUSED FROM `workspaceSettings.boardPolicy.rule.*`, NOT DECLARED INTO THIS
 * ROUTE'S FAMILY, and the reuse runs in BOTH directions already.
 * `WorkspaceBoardVisibilityPolicySection.tsx:43` holds a byte-identical
 * `POLICY_RULE_KEYS` and DRAWS these three words on the row this route SPEAKS
 * about; `WorkspaceGuestAccessSection.tsx:58-60` reuses this file's three
 * guest-rule nouns the same way. A second `Any workspace member` /
 * `Workspace admins only` / `Nobody` set under `workspaceMembers.*` would let
 * the word the page SPEAKS drift from the word the control DRAWS in every
 * language but English — which is the defect
 * `workspaceMembers.boardPolicy.visibility.private`'s comment was written
 * about.
 *
 * ⚠ THE ONLY TABLE IN THIS FILE POINTING OUTSIDE `workspaceMembers.*`, so
 * `workspace-members.routing.test.ts`'s table pin carries a per-row PREFIX
 * column rather than a hard-coded one — the shape
 * `workspace-settings.routing.test.ts:578-666` already uses for its own two
 * cross-namespace rows. The four rows that were there keep
 * `workspaceMembers.`.
 *
 * ⚠ The `Record<…>` annotation stays EXPLICIT, for the reason stated above the
 * three tables that precede it: `t()` returns the key itself on a miss, so a
 * dropped arm would speak `workspaceSettings.boardPolicy.rule.nobody` into the
 * live region with nothing red anywhere.
 */
const POLICY_RULE_KEYS: Record<BoardVisibilityPolicyRule, string> = {
  any_member: 'workspaceSettings.boardPolicy.rule.anyMember',
  admins_only: 'workspaceSettings.boardPolicy.rule.adminsOnly',
  nobody: 'workspaceSettings.boardPolicy.rule.nobody',
};

const GUEST_INVITE_RULE_KEYS: Record<WorkspaceGuestInviteRule, string> = {
  anyone: 'workspaceMembers.guests.rule.anyone',
  workspace_admins_only: 'workspaceMembers.guests.rule.adminsOnly',
  nobody: 'workspaceMembers.guests.rule.nobody',
};

/**
 * ⚠ NEW, and it closes a live defect rather than routing an existing string.
 * The visibility announcement used to interpolate `updated.visibility` — the
 * RAW WIRE VALUE — so a screen reader heard "set to public." about a control
 * one panel away (`WorkspaceSettingsPanel.tsx:176`) that had just drawn
 * `Public`. That is `boardFace.template.visibilitySet`'s recorded defect
 * verbatim, in a second file. The rendered English changes: `public` → `Public`.
 *
 * ⚠ A SECOND, SEPARATE `Private`/`Public` pair from
 * `POLICY_VISIBILITY_KEYS` above, deliberately. `WORKSPACE_VISIBILITIES` is a
 * TWO-value set about the workspace itself; `BoardVisibility` is a FOUR-value
 * set about a board's audience. `packages/shared/src/board.ts:261` and
 * `workspace.ts:108–128` keep the two vocabularies as separate declarations for
 * the same reason, and a shared `Private` would let a translation that reads
 * well for "a private workspace" rename "boards only members can see".
 */
const WORKSPACE_VISIBILITY_KEYS: Record<WorkspaceVisibility, string> = {
  private: 'workspaceMembers.settings.visibility.private',
  public: 'workspaceMembers.settings.visibility.public',
};

function WorkspaceMembersRoute() {
  const { t } = useInterfaceLanguage();
  const { workspaceId } = Route.useParams();
  const navigate = useNavigate();
  const session = useSession();
  const {
    profile,
    isPending: sessionPending,
    isSignedOut,
    error: sessionError,
  } = session;

  /*
   * A cache read, not a fetch: `enabled: false` still subscribes this component
   * to the `['workspaces']` entry, so the heading updates the moment the header
   * switcher's own `useWorkspaces()` fills it, and issues nothing of its own.
   * See the docstring — one 401-capable query on this page is a requirement,
   * not an optimisation.
   */
  const workspaces = useQuery({ ...workspacesQueryOptions(), enabled: false });
  const workspace = useQuery({
    ...workspaceQueryOptions(workspaceId),
    enabled: !isSignedOut && !sessionPending && profile !== null,
  });
  const members = useWorkspaceMembers(workspaceId);
  const roleMutation = useUpdateWorkspaceMemberRole(workspaceId);
  const addMutation = useAddWorkspaceMember(workspaceId);
  const profileMutation = useUpdateWorkspaceProfile(workspaceId);
  const visibilityMutation = useUpdateWorkspaceVisibility(workspaceId);
  const policyMutation = useUpdateWorkspaceBoardVisibilityPolicy(workspaceId);
  const guestInviteRuleMutation = useUpdateWorkspaceGuestInviteRule(workspaceId);
  const removeGuestMutation = useRemoveWorkspaceGuest(workspaceId);
  const deleteMutation = useDeleteWorkspace(workspaceId);
  const removeMutation = useRemoveWorkspaceMember(workspaceId);
  const leaveMutation = useLeaveWorkspace(workspaceId);

  /*
   * Local, and cleared on every submit, rather than derived from
   * `addMutation.data`. A v5 mutation keeps `data` across the *next* `mutate`
   * (the `pending` action clears `error`, not `data`), so a derived notice
   * would still be on screen beside the second attempt's failure, naming the
   * first attempt's member.
   *
   * ⚠ FE-80.1 — IT HOLDS THE NAME, NOT THE SENTENCE. It used to hold resolved
   * English across renders; routed naively it would hold a resolved
   * TRANSLATION, and a catalogue arriving underneath would leave the previous
   * language on screen — which no test in this repository can see
   * (`BoardMapView.tsx:80–84`, and `dragRefusalKey` in `BoardTableView.tsx`).
   * It cannot hold a bare key either, because the sentence carries user data.
   * So it holds the DATUM and `workspaceMembers.alreadyMember` is resolved at
   * render, in `messageProps` below. `WorkspaceMemberList` stays presentational
   * and keeps taking a plain string.
   */
  const [alreadyMemberName, setAlreadyMemberName] = useState<string | null>(null);
  const [removeTargetId, setRemoveTargetId] = useState<string | null>(null);
  const [memberErrors, setMemberErrors] = useState<Record<string, string>>({});
  const [joinRequestErrors, setJoinRequestErrors] = useState<Record<string, string>>({});
  const [policyErrors, setPolicyErrors] = useState<Record<string, string>>({});
  const [guestErrors, setGuestErrors] = useState<Record<string, string>>({});
  const [leaveOpen, setLeaveOpen] = useState(false);

  /*
   * §5.6: `workspaceSchema.name` carries `.max()` and no `.min(1)` — a read
   * schema describes what the database can legitimately hold — so the empty
   * string is a legal value here and must fall back exactly as a cache miss
   * does. §6.8 rule 4 fixes this heading's fallback as "Workspace members",
   * which is not §5.6's "Untitled workspace": that one names a workspace in a
   * list of workspaces, this one names a page.
   */
  const cachedName = workspaces.data?.find(
    (workspace) => workspace.id === workspaceId,
  )?.name;
  const detailName =
    workspace.data?.name !== undefined && workspace.data.name.trim() !== ''
      ? workspace.data.name
      : null;
  const cachedHeadingName =
    cachedName !== undefined && cachedName.trim() !== '' ? cachedName : null;
  const workspaceName = detailName ?? cachedHeadingName;
  const viewerRole =
    members.data === undefined || profile === null
      ? null
      : ownRole(members.data, profile.id);
  const policyEnabled = !isSignedOut && !sessionPending && profile !== null;
  /*
   * SA-9, D10: every admin surface on this page sits on `requireWorkspaceAdmin`
   * (and the policy cross-link's destination on `requireEnterpriseAdmin`), both
   * of which admit a platform superadmin with or without a row. The flag is an
   * authority, never a role: `viewerRole` keeps answering the real row, and the
   * two components below take the capability as its own prop.
   */
  const isSuper = isSuperadminSession(session);
  const adminEnabled = viewerRole === 'admin' || isSuper;
  const boardVisibilityPolicies = useWorkspaceBoardVisibilityPolicies(
    workspaceId,
    policyEnabled,
  );
  const guestInviteRule = useWorkspaceGuestInviteRule(workspaceId, adminEnabled);
  const workspaceGuests = useWorkspaceGuests(workspaceId, adminEnabled);
  const joinRequests = useWorkspaceJoinRequests(workspaceId, adminEnabled);
  const approveJoinRequest = useApproveWorkspaceJoinRequest(workspaceId);
  const declineJoinRequest = useDeclineWorkspaceJoinRequest(workspaceId);
  /*
   * `GET /workspaces/:id/enterprise` — `FE-67.2`/`FE-67.3`, `FR-PERM-034`. Is
   * this workspace inside an Enterprise, and does the caller administer that
   * Enterprise. It is the only read in the client that can answer either, and
   * without it the Enterprise policy route is a route nothing links to.
   *
   * ⚠ GATED ON `policyEnabled`, NOT ON `viewerRole`. The route's guard is
   * workspace MEMBERSHIP, and an Enterprise admin is very often a *normal*
   * member of any given workspace inside their Enterprise — `adminEnabled`
   * here would hide the link from exactly the person it exists for. Gating on
   * the session is still required, and for the reason the docstring gives at
   * length: a signed-out visit must produce ONE redirect, not two racing ones,
   * so every query on this page beyond `useWorkspaceMembers` waits for a
   * resolved profile.
   */
  const enterpriseLinkage = useWorkspaceEnterpriseLinkage(workspaceId, policyEnabled);
  /*
   * ⚠ `=== 'admin'`, never a negation, and the id is narrowed SEPARATELY
   * rather than inferred from the role. `workspaceEnterpriseLinkageSchema`
   * says `enterpriseId: null` implies `viewerEnterpriseRole: null` and
   * deliberately does not ask Zod to enforce the pairing — a `.refine()` there
   * would turn a handler bug into a 500 at serialisation time — so the pairing
   * is a route obligation, and this surface narrows what it is going to put in
   * an href rather than trusting it.
   *
   * ⚠ THIS DECIDES WHETHER THE LINK IS DRAWN AND NOTHING ELSE. It is not an
   * authorisation branch: `requireEnterpriseAdmin` folds a non-admin member, an
   * outsider and an id that never existed into ONE 404 whether or not this
   * component rendered anything, and an Enterprise id must not be probeable.
   * A failed or refused linkage read therefore draws no link and no error — the
   * affordance is additive, the member cannot act on its absence, and a Notice
   * saying so would leak that the read was attempted at all.
   */
  const linkedEnterpriseId = enterpriseLinkage.data?.enterpriseId ?? null;
  const administeredEnterpriseId =
    linkedEnterpriseId !== null &&
    (enterpriseLinkage.data?.viewerEnterpriseRole === 'admin' || isSuper)
      ? linkedEnterpriseId
      : null;
  const removeTarget =
    removeTargetId === null
      ? null
      : (members.data?.find((member) => member.id === removeTargetId) ?? null);

  function handleChangeRole(userId: string, role: WorkspaceRole) {
    roleMutation.mutate(
      { userId, role },
      {
        onSuccess: (updated) => {
          /*
           * The **settled** row's role, not the requested one: `announce()`
           * reports what happened, and the response is the only thing that
           * knows. (They agree today; a server that clamped a role would make
           * the requested value a lie, and this is the cheaper spelling.)
           */
          /*
           * ⚠ The TERNARY CHOOSES THE KEY, and both arms are written as
           * `t('…')` literals so a `t('…')` anchor can see both. A frame plus a
           * hard-coded `an admin` / `a normal member` fragment is untranslatable
           * — the article is part of the English, not of the role.
           */
          announce(
            updated.role === 'admin'
              ? t('workspaceMembers.roleChangedToAdmin', {
                  name: profileName(updated),
                })
              : t('workspaceMembers.roleChangedToNormal', {
                  name: profileName(updated),
                }),
          );
        },
      },
    );
  }

  function handleAddMember(handle: { email: string } | { username: string }) {
    setAlreadyMemberName(null);
    addMutation.mutate(handle, {
      onSuccess: (result) => {
        const name = profileName(result.member);
        if (result.added) {
          /*
           * ⚠ The announcement gets its **own** fallback, not the heading's.
           * Sharing `workspaceName`'s would announce "Ada Lovelace added to
           * Workspace members." on a cache miss — a page title read as a
           * workspace name. The brief fixes the sentence as "{displayName}
           * added to {workspaceName}." and does not say what happens when the
           * name is not known; this is that answer.
           */
          announce(
            workspaceName === null
              ? t('workspaceMembers.addedFallback', { name })
              : t('workspaceMembers.added', { name, workspace: workspaceName }),
          );
          return;
        }
        /*
         * `added === false` — the no-op re-add. Stated, never reported as a
         * success and never as an error (`FR-PERM-021` AC4).
         *
         * ⚠ **This deliberately does NOT also call `announce()`, and the
         * integration pass removed the call that was here.** The two outcomes
         * of this one mutation take different channels because they render
         * differently: a real add renders nothing of its own, so `announce()`
         * is its only channel; a re-add renders `Notice tone="info"`, which
         * **is** a `role="status"` node, so announcing as well put the identical
         * sentence into the accessibility tree twice — once in the notice and
         * once in the shell's live region. A screen reader says it twice, and
         * `D2-BRIEF.md` §7 rule 4 forbids exactly that: one channel per event.
         *
         * It was found by a test, not by reading: a page-wide `findByText`
         * for the sentence matched two nodes and resolved to the wrong one.
         *
         * ⚠ The known cost, recorded rather than hidden: a `role="status"` node
         * that is **inserted** already carrying its text is announced less
         * reliably than text written into a region that was already mounted.
         * If that proves to be a real loss, the fix is a channel decision in
         * `Notice` — not a second `announce()` here.
         */
        setAlreadyMemberName(name);
      },
    });
  }

  function clearJoinRequestError(requestId: string) {
    setJoinRequestErrors((previous) => {
      const { [requestId]: _discard, ...next } = previous;
      return next;
    });
  }

  function handleApproveJoinRequest(requestId: string) {
    clearJoinRequestError(requestId);
    approveJoinRequest.mutate(
      { requestId },
      {
        onSuccess: (request) => {
          announce(
            t('workspaceMembers.joinRequestApproved', {
              name: profileName(request.user),
            }),
          );
        },
        onError: (error) => {
          setJoinRequestErrors((previous) => ({
            ...previous,
            [requestId]: error.message,
          }));
        },
      },
    );
  }

  function handleDeclineJoinRequest(requestId: string) {
    clearJoinRequestError(requestId);
    declineJoinRequest.mutate(
      { requestId },
      {
        onSuccess: (request) => {
          announce(
            t('workspaceMembers.joinRequestDeclined', {
              name: profileName(request.user),
            }),
          );
        },
        onError: (error) => {
          setJoinRequestErrors((previous) => ({
            ...previous,
            [requestId]: error.message,
          }));
        },
      },
    );
  }

  function handleRemoveMember(userId: string) {
    setMemberErrors((previous) => {
      const { [userId]: _discard, ...next } = previous;
      return next;
    });
    setRemoveTargetId(userId);
  }

  function handleConfirmRemove(removeFromBoards: boolean) {
    if (removeTarget === null) return;
    const member = removeTarget;
    setMemberErrors((previous) => {
      const { [member.id]: _discard, ...next } = previous;
      return next;
    });
    removeMutation.mutate(
      { userId: member.id, removeFromBoards },
      {
        onSuccess: () => {
          announce(t('workspaceMembers.removed', { name: profileName(member) }));
          setRemoveTargetId(null);
        },
        onError: (error) => {
          setMemberErrors((previous) => ({ ...previous, [member.id]: error.message }));
        },
      },
    );
  }

  function handleProfileChange(patchBody: WorkspaceProfilePatch) {
    profileMutation.mutate(patchBody, {
      onSuccess: () => {
        announce(t('workspaceMembers.settings.profileSaved'));
      },
    });
  }

  function clearPolicyError(
    visibility: BoardVisibility,
    field: 'createRule' | 'deleteRule',
  ) {
    const key = policyKey(visibility, field);
    setPolicyErrors((previous) => {
      const { [key]: _discard, [visibility]: _discardVisibility, ...next } = previous;
      return next;
    });
  }

  function handleChangePolicy(
    visibility: BoardVisibility,
    field: 'createRule' | 'deleteRule',
    rule: BoardVisibilityPolicyRule,
  ) {
    clearPolicyError(visibility, field);
    policyMutation.mutate(
      {
        visibility,
        body: field === 'createRule' ? { createRule: rule } : { deleteRule: rule },
      },
      {
        /*
         * ⚠ THE RESPONSE IS READ, and until `FE-67.2`'s last arm this callback
         * took no parameter at all. `useUpdateWorkspaceBoardVisibilityPolicy`
         * resolves to the FOLDED row — the PATCH handler answers through the
         * same `attribute()` the GET does — so the Enterprise attribution an
         * admin needs is already on the wire and was being thrown away here.
         */
        onSuccess: (updated) => {
          /*
           * ⚠ The FIELD chooses the key; the VISIBILITY fills a slot. The two
           * halves of the old template literal are ruled on differently and
           * deliberately — see `POLICY_VISIBILITY_KEYS` above. All four arms are
           * `t('…')` literals for the anchor.
           */
          const visibilityLabel = t(POLICY_VISIBILITY_KEYS[visibility]);
          /*
           * ⚠ `=== 'enterprise'`, NEVER A NEGATION. `createRuleSource` is
           * `.optional()` on the shared schema and absent means "fall back to
           * `source`", which is only `'default' | 'workspace'` — so
           * `!== 'workspace'` would speak the override sentence on every
           * un-governed save, and every fixture in the repository omits the
           * field, so it would be green everywhere and wrong only under a real
           * Enterprise.
           */
          const overridden =
            field === 'createRule'
              ? updated.createRuleSource === 'enterprise'
              : updated.deleteRuleSource === 'enterprise';
          /*
           * ⚠ The EFFECTIVE rule, which is what the sentence is FOR. The control
           * the admin just used shows the WORKSPACE rule the PATCH writes; this
           * names what is enforced over it. `fold()` resolves ties to the
           * workspace (`>`, never `>=`), so on the `'enterprise'` arm the two
           * are provably different — and the `??` fallback is the fourth case,
           * an absent field, which cannot be reached on this arm but keeps the
           * expression total without a non-null assertion.
           */
          const enforced =
            field === 'createRule'
              ? (updated.effectiveCreateRule ?? updated.createRule)
              : (updated.effectiveDeleteRule ?? updated.deleteRule);
          /*
           * ⚠ ONE `announce()` CARRYING BOTH FACTS, and that is forced rather
           * than chosen. `announce()` is last-write-wins — one module-level
           * region, one pending message — so appending the override as a second
           * call would overwrite "…policy saved." before a screen reader reached
           * either sentence. Four cases, one call, one string.
           */
          announce(
            overridden
              ? field === 'createRule'
                ? t('workspaceMembers.boardPolicy.createSavedEnterprise', {
                    visibility: visibilityLabel,
                    rule: t(POLICY_RULE_KEYS[enforced]),
                  })
                : t('workspaceMembers.boardPolicy.deleteSavedEnterprise', {
                    visibility: visibilityLabel,
                    rule: t(POLICY_RULE_KEYS[enforced]),
                  })
              : field === 'createRule'
                ? t('workspaceMembers.boardPolicy.createSaved', {
                    visibility: visibilityLabel,
                  })
                : t('workspaceMembers.boardPolicy.deleteSaved', {
                    visibility: visibilityLabel,
                  }),
          );
        },
        onError: (error) => {
          setPolicyErrors((previous) => ({
            ...previous,
            [policyKey(visibility, field)]: error.message,
          }));
        },
      },
    );
  }

  function handleChangeGuestInviteRule(rule: WorkspaceGuestInviteRule) {
    guestInviteRuleMutation.mutate(rule, {
      onSuccess: () => {
        announce(
          t('workspaceMembers.guests.ruleSaved', {
            rule: t(GUEST_INVITE_RULE_KEYS[rule]),
          }),
        );
      },
    });
  }

  function handleRemoveGuest(userId: string) {
    setGuestErrors((previous) => {
      const { [userId]: _discard, ...next } = previous;
      return next;
    });
    const guest = workspaceGuests.data?.find((candidate) => candidate.id === userId);
    removeGuestMutation.mutate(
      { userId },
      {
        onSuccess: () => {
          announce(
            guest === undefined
              ? t('workspaceMembers.guests.removedFallback')
              : t('workspaceMembers.guests.removed', { name: profileName(guest) }),
          );
        },
        onError: (error) => {
          setGuestErrors((previous) => ({ ...previous, [userId]: error.message }));
        },
      },
    );
  }

  function handleDeleteWorkspace(confirmName: string) {
    deleteMutation.mutate(
      { confirmName },
      {
        onSuccess: () => {
          announce(t('workspaceMembers.settings.deleted'));
          void navigate({ to: '/' });
        },
      },
    );
  }

  function handleLeaveWorkspace() {
    leaveMutation.mutate(undefined, {
      onSuccess: () => {
        announce(t('workspaceMembers.leave.announced'));
        setLeaveOpen(false);
        void navigate({ to: '/' });
      },
    });
  }

  if (isSignedOut) {
    return (
      <MembersPage title={t('workspaceMembers.title')}>
        <EmptyState
          tone="neutral"
          size="panel"
          headingLevel={2}
          title={t('workspaceMembers.signInTitle')}
          body={t('workspaceMembers.signInBody')}
          action={
            <Button
              as="a"
              variant="primary"
              /*
               * Built from the route's own params rather than
               * `window.location`, so it is correct under the memory history
               * every test uses, and encoded the one way `safeNextPath` reads
               * back. `search: ''` — this route takes no search params.
               */
              href={signInHref({
                pathname: `/w/${workspaceId}/members`,
                search: '',
              })}
            >
              {t('workspaceMembers.signInAction')}
            </Button>
          }
        />
      </MembersPage>
    );
  }

  if (sessionPending) {
    return (
      <MembersPage title={t('workspaceMembers.title')}>
        <MembersPending />
      </MembersPage>
    );
  }

  if (profile === null) {
    /*
     * Not pending, not signed out, and still no profile: the probe failed for
     * some other reason. `tone="error"`, never `neutral` — `FR-EXP-034`'s
     * second edge case in as many words.
     */
    return (
      <MembersPage title={t('workspaceMembers.title')}>
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={2}
          title={t('workspaceMembers.accountErrorTitle')}
          body={sessionError?.message ?? t('workspaceMembers.genericError')}
        />
      </MembersPage>
    );
  }

  const title = workspaceName ?? t('workspaceMembers.title');

  if (members.isPending) {
    return (
      <MembersPage title={title}>
        <MembersPending />
      </MembersPage>
    );
  }

  if (members.isError) {
    const notFound = members.error.code === 'not_found';
    return (
      <MembersPage title={title}>
        <EmptyState
          tone="error"
          size="panel"
          headingLevel={2}
          title={
            notFound
              ? t('workspaceMembers.notFoundTitle')
              : t('workspaceMembers.loadFailedTitle')
          }
          {...(notFound ? {} : { body: members.error.message })}
        />
      </MembersPage>
    );
  }

  /*
   * `exactOptionalPropertyTypes`: `roleError`, `addError` and `addNotice` are
   * declared `?: string` with no `| undefined`, so only an **omitted** key
   * satisfies them — `roleError={maybeUndefined}` does not compile. Typed
   * against the component's own props rather than left inferred, because a
   * bare conditional spread bypasses excess-property checking and a mistyped
   * key would compile to a prop nothing reads.
   */
  const messageProps: Pick<
    WorkspaceMemberListProps,
    'roleError' | 'addError' | 'addNotice' | 'memberErrors'
  > = {
    ...(roleMutation.error === null ? {} : { roleError: roleMutation.error.message }),
    ...(addMutation.error === null ? {} : { addError: addMutation.error.message }),
    /*
     * ⚠ RESOLVED HERE, at render, from the NAME the state holds — never stored
     * resolved. See `alreadyMemberName`'s own note above.
     */
    ...(alreadyMemberName === null
      ? {}
      : {
          addNotice: t('workspaceMembers.alreadyMember', {
            name: alreadyMemberName,
          }),
        }),
    ...(Object.keys(memberErrors).length === 0 ? {} : { memberErrors }),
  };
  const policyBusyKey =
    policyMutation.isPending && policyMutation.variables !== undefined
      ? policyKey(
          policyMutation.variables.visibility,
          'createRule' in policyMutation.variables.body ? 'createRule' : 'deleteRule',
        )
      : null;
  const policyProps: Pick<
    WorkspaceBoardVisibilityPolicySectionProps,
    'busyKey' | 'errors'
  > = {
    ...(policyBusyKey === null ? {} : { busyKey: policyBusyKey }),
    ...(Object.keys(policyErrors).length === 0 ? {} : { errors: policyErrors }),
  };
  const guestAccessProps: Pick<
    WorkspaceGuestAccessSectionProps,
    'ruleError' | 'guestErrors'
  > = {
    ...(guestInviteRuleMutation.error !== null
      ? { ruleError: guestInviteRuleMutation.error.message }
      : guestInviteRule.isError
        ? { ruleError: guestInviteRule.error.message }
        : {}),
    ...(Object.keys(guestErrors).length === 0 ? {} : { guestErrors }),
  };

  return (
    <MembersPage title={title}>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(22rem,28rem)]">
        <WorkspaceMemberList
          members={members.data}
          /*
           * ⚠ The caller's own role comes from the list itself — route 4 returns
           * every member including the caller, and no other route in the client
           * carries it. `null` (session not resolved) renders read-only: a
           * control that appears a moment late beats one that appears and 403s.
           */
          viewerRole={viewerRole}
          viewerCanAdminister={adminEnabled}
          viewerId={profile.id}
          onChangeRole={handleChangeRole}
          onAddMember={handleAddMember}
          onRemoveMember={handleRemoveMember}
          /*
           * `variables` is the in-flight row, so the busy flag follows the
           * mutation rather than a second piece of state that could disagree
           * with it. Only one role change is ever in flight from this surface.
           */
          busyRoleFor={
            roleMutation.isPending ? (roleMutation.variables?.userId ?? null) : null
          }
          busyMemberId={
            removeMutation.isPending ? (removeMutation.variables?.userId ?? null) : null
          }
          addBusy={addMutation.isPending}
          {...messageProps}
        />
        <div className="flex flex-col gap-6">
          {workspace.isPending ? (
            <MembersPending />
          ) : workspace.isError ? (
            <EmptyState
              tone="error"
              size="panel"
              headingLevel={2}
              title={t('workspaceMembers.settings.loadFailedTitle')}
              body={workspace.error.message}
            />
          ) : (
            <WorkspaceSettingsPanel
              workspace={workspace.data}
              viewerRole={viewerRole}
              viewerCanAdminister={adminEnabled}
              onProfileChange={handleProfileChange}
              onVisibilityChange={(visibility) =>
                visibilityMutation.mutate(visibility, {
                  onSuccess: (updated) => {
                    /*
                     * ⚠ The LABEL, not `updated.visibility` — see
                     * `WORKSPACE_VISIBILITY_KEYS`. The settled row's value, not
                     * the requested one: `announce()` reports what happened.
                     */
                    announce(
                      t('workspaceMembers.settings.visibilitySaved', {
                        visibility: t(WORKSPACE_VISIBILITY_KEYS[updated.visibility]),
                      }),
                    );
                  },
                })
              }
              onDeleteWorkspace={handleDeleteWorkspace}
              onLeaveWorkspace={() => setLeaveOpen(true)}
              busy={{
                profile: profileMutation.isPending,
                visibility: visibilityMutation.isPending,
                delete: deleteMutation.isPending,
                leave: leaveMutation.isPending,
              }}
              error={{
                ...(profileMutation.error === null
                  ? {}
                  : { profile: profileMutation.error.message }),
                ...(visibilityMutation.error === null
                  ? {}
                  : { visibility: visibilityMutation.error.message }),
                ...(deleteMutation.error === null
                  ? {}
                  : { delete: deleteMutation.error.message }),
                ...(leaveMutation.error === null
                  ? {}
                  : { leave: leaveMutation.error.message }),
              }}
            />
          )}

          <WorkspaceBoardVisibilityPolicySection
            policies={boardVisibilityPolicies.data}
            loading={boardVisibilityPolicies.isPending}
            canManage={adminEnabled}
            onChangePolicy={handleChangePolicy}
            {...policyProps}
          />
          {boardVisibilityPolicies.isError ? (
            <Notice tone="bad" body={boardVisibilityPolicies.error.message} />
          ) : null}

          {adminEnabled ? (
            <>
              <WorkspaceGuestAccessSection
                guestInviteRule={guestInviteRule.data?.guestInviteRule}
                guests={workspaceGuests.data}
                loading={guestInviteRule.isPending || workspaceGuests.isPending}
                canManage
                ruleBusy={guestInviteRuleMutation.isPending}
                removeBusyId={
                  removeGuestMutation.isPending
                    ? (removeGuestMutation.variables?.userId ?? null)
                    : null
                }
                onChangeGuestInviteRule={handleChangeGuestInviteRule}
                onRemoveGuest={handleRemoveGuest}
                {...guestAccessProps}
              />
              {workspaceGuests.isError ? (
                <Notice tone="bad" body={workspaceGuests.error.message} />
              ) : null}
            </>
          ) : null}

          {adminEnabled ? (
            <WorkspaceJoinRequestsSection
              requests={joinRequests.data}
              loading={joinRequests.isPending}
              canManage
              approveBusyId={
                approveJoinRequest.isPending
                  ? (approveJoinRequest.variables?.requestId ?? null)
                  : null
              }
              declineBusyId={
                declineJoinRequest.isPending
                  ? (declineJoinRequest.variables?.requestId ?? null)
                  : null
              }
              errors={joinRequestErrors}
              onApprove={handleApproveJoinRequest}
              onDecline={handleDeclineJoinRequest}
            />
          ) : null}

          {/*
            `FE-67.2`/`FE-67.3` — the one link in the product to
            `/enterprises/:id/policy`. Drawn last in the settings column
            because it leaves this workspace: everything above it is settable
            here, and this is the pointer to the surface that overrides it.

            ⚠ `Button as="a" href`, not the router's typed `Link`, on
            PRECEDENT rather than on a constraint. `/enterprises/$enterpriseId/policy`
            IS in the generated tree — a typed `to=` compiles today, and it was
            measured rather than assumed. But `Button as="a"` is what
            twenty-one call sites across this client already do (`Link` is
            imported by four files, all of them shell chrome), and the sign-in
            affordance twenty lines up is the same shape in this same file. The
            cost is honest and stated: an `<a href>` inside TanStack Router is a
            full document load, not a client navigation. It is paid on a link a
            member follows once to leave this page for a different section of
            the product, and the shipped pattern wins over saving it.
          */}
          {administeredEnterpriseId === null ? null : (
            <section
              className="flex flex-col gap-3"
              aria-labelledby="workspace-enterprise-heading"
            >
              <h2
                id="workspace-enterprise-heading"
                className="font-display text-lg font-semibold text-ink"
              >
                {t('enterpriseAdmin.workspaceLink.heading')}
              </h2>
              <p className="text-sm text-ink-muted">
                {t('enterpriseAdmin.workspaceLink.body')}
              </p>
              <div>
                <Button
                  as="a"
                  variant="secondary"
                  href={`/enterprises/${encodeURIComponent(administeredEnterpriseId)}/policy`}
                >
                  {t('enterpriseAdmin.workspaceLink.action')}
                </Button>
              </div>
            </section>
          )}
        </div>
      </div>
      <WorkspaceMemberRemoveDialog
        member={removeTarget}
        busy={removeMutation.isPending}
        error={removeTarget === null ? null : (memberErrors[removeTarget.id] ?? null)}
        onConfirm={handleConfirmRemove}
        onCancel={() => setRemoveTargetId(null)}
      />
      <LeaveWorkspaceDialog
        open={leaveOpen}
        busy={leaveMutation.isPending}
        error={leaveMutation.error?.message ?? null}
        onConfirm={handleLeaveWorkspace}
        onCancel={() => setLeaveOpen(false)}
      />
    </MembersPage>
  );
}

function LeaveWorkspaceDialog({
  open,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  /*
   * Its own `useInterfaceLanguage()`, not a `t` threaded down as a prop —
   * `routes/profile.tsx:141` and `:160` are the shipped precedent for two hook
   * calls in one route file.
   */
  const { t } = useInterfaceLanguage();

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      title={t('workspaceMembers.leave.title')}
      description={t('workspaceMembers.leave.description')}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            {t('workspaceMembers.leave.cancel')}
          </Button>
          <Button variant="danger" busy={busy} onClick={onConfirm}>
            {t('workspaceMembers.leave.confirm')}
          </Button>
        </>
      }
    >
      {error === null ? null : (
        <p role="alert" className="text-sm text-bad">
          {error}
        </p>
      )}
    </Dialog>
  );
}
